/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import { DebouncedTextField, useKeyPress, useOnClickOutside } from "@cd2/components"
import { Form2 } from "@cd2/forms"
import { useTheme } from "@cd2/theme"
import React, { useEffect, useRef, useState } from "react"
import { IPostcoderSuggestion, usePostcodeLookup } from "../../../hooks/usePostcodeLookup"
import { useSiteAlerts } from "../../../hooks/useSiteAlerts"

export interface IPostcodeLookupReturnValue {
  city: string
  country: string
  county: string
  name: string
  line_1: string
  line_2: string
  line_3: string
  line_4: string
  postcode: string
}

interface IPostcodeProps {
  label?: React.ReactNode
  placeholder?: string
  postcodeFieldError?: string
  onPostcodeSelect(val: IPostcodeLookupReturnValue): void
  field?: string
}

export function PostcodeField({
  onPostcodeSelect,
  label,
  placeholder,
  postcodeFieldError,
  field,
}: IPostcodeProps) {
  const postcodeField = Form2.useField<string>(field ?? "postcode", { defaultValue: "" })
  const { searchPostcode, suggestions, validPostcode, clear, getOption } = usePostcodeLookup()
  const alert = useSiteAlerts()
  const { forms, borderRadius } = useTheme()
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [focused, setFocused] = useState(false)
  const [previousValue, setPreviousValue] = useState("")
  const [prompt, setPrompt] = useState<string | null>(null)
  const isSearching = focused && suggestions.length > 0

  const ref = useRef<HTMLDivElement>(null)
  useOnClickOutside(ref, () => setFocused(false))

  useKeyPress("ArrowUp", handleUpKeyPress)
  useKeyPress("ArrowDown", handleDownKeyPress)
  useKeyPress("Enter", handleEnterKeyPress)

  useEffect(() => {
    if (
      !postcodeField.getValue() ||
      postcodeField.getValue().toLowerCase() === previousValue.toLowerCase()
    ) {
      return
    }
    if (postcodeField.getValue().length > 4) {
      if (validPostcode(postcodeField.getValue())) {
        handlePostcodeSearch()
      } else {
        setPrompt("Please enter a valid postcode")
      }
    } else {
      clear()
      setPrompt(null)
    }
    setPreviousValue(postcodeField.getValue())
  }, [postcodeField.getValue()])

  function handleUpKeyPress() {
    if (isSearching) {
      if (highlightedIndex === 0) {
        setHighlightedIndex(suggestions.length - 1)
      } else {
        setHighlightedIndex(highlightedIndex - 1)
      }
    }
  }

  function handleDownKeyPress() {
    if (isSearching) {
      if (highlightedIndex === suggestions.length - 1) {
        setHighlightedIndex(0)
      } else {
        setHighlightedIndex(highlightedIndex + 1)
      }
    }
  }

  function handleEnterKeyPress() {
    if (isSearching) {
      handleOptionSelect(suggestions[highlightedIndex])
    }
  }

  async function handleOptionSelect(value: IPostcoderSuggestion) {
    const result = await getOption(value)
    onPostcodeSelect(result.data?.[0])
    setFocused(false)
    clear()
  }

  async function handlePostcodeSearch() {
    const val = postcodeField.getValue()
    if (val && validPostcode(val)) {
      const suggestions = await searchPostcode(val)
      if (typeof suggestions !== "boolean" && suggestions.length === 0) {
        setPrompt("There are no results for that postcode")
      } else {
        setPrompt(null)
      }
    } else {
      alert.genericError("Searching requires a valid postcode, please check and try again")
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault()
    }
  }

  return (
    <div
      ref={ref}
      css={css`
        position: relative;
        display: inline-block;
        width: 100%;
      `}
    >
      <DebouncedTextField
        value={postcodeField.getValue()}
        onChange={postcodeField.setValue}
        label={label ?? "Postcode"}
        onFocus={() => setFocused(true)}
        inputProps={{ autoComplete: "DISABLED", onKeyDown: handleKeyDown }}
        placeholder={placeholder}
        error={postcodeFieldError}
      />
      {prompt && focused && (
        <div
          css={css`
            position: absolute;
            z-index: 100;
            margin-top: 3px;
            width: 100%;
            background-color: #e5e5e5;
            border-radius: ${borderRadius}px;
            border: 1px solid ${forms.border.color};
            overflow: hidden;
            padding: 12px;
            font-size: 12px;
            color: rgba(0, 0, 0, 0.75);
            font-weight: 600;
          `}
        >
          {prompt}
        </div>
      )}
      {isSearching && (
        <div
          css={css`
            position: absolute;
            z-index: 100;
            margin-top: 3px;
            width: 100%;
            background-color: white;
            border-radius: ${borderRadius}px;
            border: 1px solid ${forms.border.color};
            overflow: hidden;
          `}
        >
          {suggestions.map((option, i) => (
            <div
              key={option.id}
              css={css`
                padding: 12px;

                &:hover {
                  background-color: ${highlightedIndex === i ? "#e5e5e5" : "#f5f5f5"};
                }

                background-color: ${highlightedIndex === i ? "#e5e5e5" : "white"};
                cursor: pointer;

                &:not(:first-child) {
                  border-top: 1px solid #eee;
                }
              `}
              onClick={() => handleOptionSelect(option)}
            >
              <span
                css={css`
                  display: block;
                  margin-bottom: 4px;
                  font-size: 13px;
                `}
              >
                <span>{option.summaryline}</span>
                <br />
                {option.locationsummary}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
